import { useState, useRef } from "react";
import { Button, Modal, Slider, Typography, Space, Dropdown } from "antd";
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  DownloadOutlined,
  FullscreenOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import jsPDF from "jspdf";

const ImageViewer = ({
  visible,
  onClose,
  imageData,
  title,
  onSwitchDrawing,
  currentDrawingType,
}) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 5));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.25));
  };

  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDownload = (format = "png") => {
    if (imageData) {
      if (format === "pdf") {
        // Create PDF from the image
        const img = new Image();
        img.onload = () => {
          const pdf = new jsPDF({
            orientation: img.width > img.height ? "landscape" : "portrait",
            unit: "px",
            format: [img.width, img.height],
          });
          pdf.addImage(imageData, "PNG", 0, 0, img.width, img.height);
          pdf.save(`${title || "drawing"}.pdf`);
        };
        img.src = imageData;
      } else {
        // Download as image (PNG or JPG)
        const link = document.createElement("a");
        link.download = `${title || "drawing"}.${format}`;

        if (format === "jpg") {
          // Convert PNG to JPG by creating a canvas
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          const img = new Image();
          img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            link.href = canvas.toDataURL("image/jpeg", 0.9);
            link.click();
          };
          img.src = imageData;
        } else {
          // PNG format
          link.href = imageData;
          link.click();
        }
      }
    }
  };

  const downloadMenuItems = [
    {
      key: "png",
      label: "Download as PNG",
      onClick: () => handleDownload("png"),
    },
    {
      key: "jpg",
      label: "Download as JPG",
      onClick: () => handleDownload("jpg"),
    },
    {
      key: "pdf",
      label: "Download as PDF",
      onClick: () => handleDownload("pdf"),
    },
  ];

  const handleFullscreen = () => {
    if (containerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        containerRef.current.requestFullscreen();
      }
    }
  };

  const handleSwitchDrawing = () => {
    if (onSwitchDrawing) {
      onSwitchDrawing();
    }
  };

  return (
    <Modal
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 30,
          }}
        >
          <Typography.Text strong>{title || "Image Viewer"}</Typography.Text>
          <Space>
            <Button
              icon={<ZoomOutOutlined />}
              onClick={handleZoomOut}
              disabled={scale <= 0.25}
              size="small"
            />
            <Typography.Text style={{ minWidth: 60, textAlign: "center" }}>
              {Math.round(scale * 100)}%
            </Typography.Text>
            <Button
              icon={<ZoomInOutlined />}
              onClick={handleZoomIn}
              disabled={scale >= 5}
              size="small"
            />
            <Button onClick={handleReset} size="small">
              Reset
            </Button>
            <Dropdown
              menu={{ items: downloadMenuItems }}
              placement="bottomRight"
            >
              <Button icon={<DownloadOutlined />} size="small" />
            </Dropdown>
            <Button
              icon={<FullscreenOutlined />}
              onClick={handleFullscreen}
              size="small"
            />
            {onSwitchDrawing && (
              <Button
                icon={<SwapOutlined />}
                onClick={handleSwitchDrawing}
                size="small"
              >
                To {currentDrawingType === "network" ? "Wiring" : "Network"}
              </Button>
            )}
          </Space>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width="90vw"
      style={{ top: 20 }}
      styles={{
        body: {
          height: "80vh",
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f0f0f0",
        },
      }}
      footer={null}
    >
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          position: "relative",
          cursor: isDragging ? "grabbing" : scale > 1 ? "grab" : "default",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: "center center",
            transition: isDragging ? "none" : "transform 0.1s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          {imageData && (
            <img
              src={imageData}
              alt={title || "Drawing"}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                userSelect: "none",
                pointerEvents: "none",
              }}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ImageViewer;
